import { useState } from "react";
import { motion } from "framer-motion";
import { Keyboard, Search, Command, ArrowUp, CornerDownLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";

const shortcuts = [
  {
    category: "Navigation",
    items: [
      { keys: ["G", "D"], desc: "Go to Dashboard" },
      { keys: ["G", "C"], desc: "Go to My Courses" },
      { keys: ["G", "P"], desc: "Go to My Progress" },
      { keys: ["G", "S"], desc: "Go to Settings" },
      { keys: ["G", "H"], desc: "Go to Help & Support" },
      { keys: ["G", "N"], desc: "Go to Notifications" },
    ],
  },
  {
    category: "Global",
    items: [
      { keys: ["/"], desc: "Focus search bar" },
      { keys: ["Esc"], desc: "Close dialog / modal" },
      { keys: ["?"], desc: "Show keyboard shortcuts" },
      { keys: ["N"], desc: "Toggle notification panel" },
    ],
  },
  {
    category: "Course Player",
    items: [
      { keys: ["\u2190"], desc: "Previous slide / section" },
      { keys: ["\u2192"], desc: "Next slide / section" },
      { keys: ["Space"], desc: "Play / Pause video" },
      { keys: ["F"], desc: "Toggle fullscreen" },
      { keys: ["M"], desc: "Mute / Unmute" },
    ],
  },
  {
    category: "Actions",
    items: [
      { keys: ["\u2318", "Enter"], desc: "Submit form / Save" },
      { keys: ["\u2318", "K"], desc: "Command palette" },
      { keys: ["\u2318", "B"], desc: "Toggle sidebar" },
    ],
  },
];

const KeyCap = ({ children }) => (
  <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-md bg-muted border border-border shadow-[0_1px_0_1px_hsl(var(--border))] text-xs font-mono font-semibold text-foreground">
    {children}
  </span>
);

const KeyboardShortcutsPage = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const isLearner = user?.role === "learner";
  const variant = isLearner ? "learner" : user?.role === "tenant_admin" ? "tenant_admin" : "super_admin";

  const filteredSections = shortcuts.map(s => ({
    ...s,
    items: s.items.filter(i => search ? i.desc.toLowerCase().includes(search.toLowerCase()) || i.keys.join("").toLowerCase().includes(search.toLowerCase()) : true),
  })).filter(s => s.items.length > 0);

  return (
    <AdminLayout title="Keyboard Shortcuts" variant={variant}>
      <div className="space-y-6 max-w-3xl" data-testid="keyboard-shortcuts-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: "'Sora', sans-serif" }}>
            <Keyboard className="w-7 h-7 text-primary" /> Keyboard Shortcuts
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Speed up your workflow with these shortcuts</p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search shortcuts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-9"
            data-testid="shortcut-search"
          />
        </div>

        {filteredSections.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground"><Keyboard className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No shortcuts match your search</p></CardContent></Card>
        ) : (
          <div className="space-y-6">
            {filteredSections.map((section, si) => (
              <motion.div
                key={si}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: si * 0.05 }}
              >
                <Card data-testid={`shortcut-section-${si}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{section.category}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="divide-y">
                      {section.items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between py-3" data-testid={`shortcut-${si}-${i}`}>
                          <span className="text-sm">{item.desc}</span>
                          <div className="flex items-center gap-1">
                            {item.keys.map((key, ki) => (
                              <span key={ki} className="flex items-center gap-1">
                                {ki > 0 && <span className="text-xs text-muted-foreground mx-0.5">+</span>}
                                <KeyCap>{key}</KeyCap>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tip */}
        <Card className="bg-primary/5 border-primary/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Command className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Pro Tip</p>
              <p className="text-xs text-muted-foreground">
                Press <KeyCap>?</KeyCap> anywhere in the app to quickly open this shortcuts reference.
                Use <KeyCap>{"/"}</KeyCap> to jump to the search bar instantly.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default KeyboardShortcutsPage;
